import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/PageCommon.css';
import './AssortmentPage.css';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload-icon.svg';
import { ReactComponent as DropdownIcon } from '../../assets/icons/dropdown-icon.svg';
import { ReactComponent as ActiveIcon } from '../../assets/icons/active-icon.svg';
import { ReactComponent as NewIcon } from '../../assets/icons/new-icon.svg';
import { ReactComponent as TbdICon } from '../../assets/icons/tbd-icon.svg';
import { ReactComponent as DiscnIcon } from '../../assets/icons/discn-icon.svg';
import UploadExcelModal from '../../components/common/UploadAssortmentsExcelModal/UploadExcelModal.js';
import EditableCell, { EditableCellActions } from '../../components/common/EditableCell';
import { assortmentService } from '../../services/assortmentService.js';
import { formatDate } from '../../utils/utils';
import ReactDOM from 'react-dom';
import { NotificationContainer } from '../../components/common/NotificationContainer.js';
import NotificationModal from '../../components/common/NotificationModal.js';
import { parseApiError } from '../../utils/parseErrorMessage.js';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation.js';
import Pagination from '../../components/common/Pagination/Pagination.js';
import { useFilterParams } from '../../hooks/useFilterParams.js';
import ColumnFilterDropdown from '../../components/common/ColumnFilterDropdown';

const StatusDropdownPortal = ({ anchorEl, options, onSelect, onClose }) => {
  const [position, setPosition] = useState({ top: 0, right: 0 });

  const computePosition = (el) => {
    const rect = el.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY,
      right: window.innerWidth - rect.right,
    };
  };

  useEffect(() => {
    if (anchorEl) setPosition(computePosition(anchorEl));
  }, [anchorEl]);

  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) setPosition(computePosition(anchorEl));
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [anchorEl]);

  return ReactDOM.createPortal(

    <div className="dropdown-container"
      style={{
        position: 'absolute',
        top: position.top,
        right: position.right,
        zIndex: 99999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((option) => (
        <div
          key={option}
          className="dropdown-container-option"
          onClick={() => {onSelect(option); onClose();}}
        >
          <div className="dropdown-container-option-content">
            {
              option.toLowerCase() === 'активный'.toLowerCase() ? <ActiveIcon width={20}/> 
                : option.toLowerCase() === 'новый'.toLowerCase() ? <NewIcon width={20}/> 
                  : option.toLowerCase() === 'неактивный'.toLowerCase() ? <DiscnIcon width={20}/> 
                    : option.toLowerCase() === 'на вывод'.toLowerCase() ? <TbdICon width={20}/> 
                      : <span style={{width: "20px"}}></span>
            }
            <span style={{
              color: option.toLowerCase() !== 'активный'.toLowerCase() && 
                option.toLowerCase() !== 'новый'.toLowerCase() && 
                option.toLowerCase() !== 'на вывод'.toLowerCase() && 
                option.toLowerCase() !== 'неактивный'.toLowerCase() 
                  ? "#DC1A00" 
                  : "#404040"
            }}>
              {option}
            </span>
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

const COLUMNS_ALL = [
  { key: 'sku_code',                    label: 'Код СКЮ' },
  { key: 'sku_name',                    label: 'Наименование' },
  { key: 'mother_sku',                  label: 'Базовый СКЮ' },
  { key: 'pieces_in_master_carton',     label: 'Кол-во штук в коробке' },
  { key: 'master_carton_volume_cbm',    label: 'Объем (м3)' },
  { key: 'master_carton_gross_weight_kg', label: 'Вес брутто (кг)' },
  { key: 'lead_time',                   label: 'Время доставки (дн)' },
  { key: 'source',                      label: 'Источник' },
  { key: 'general_stock_norm_days',     label: 'Общая норма стока (дни)' },
  { key: 'status',                      label: 'Статус' },
];

const COLUMNS_BRANCHES = [
  { key: 'sku_code',  label: 'Код СКЮ' },
  { key: 'sku_name',  label: 'Наименование' },
  { key: 'mother_sku', label: 'Базовый СКЮ' },
];

const COLUMNS_PRICE = [
  { key: 'sku_code',      label: 'Код СКЮ' },
  { key: 'sku_name',      label: 'Наименование' },
  { key: 'date',          label: 'Дата' },
  { key: 'invoice_price', label: 'Инвойс цена за единицу' },
  { key: 'dsp',           label: 'Прайс-лист за единицу' },
];

const DEFAULT_FILTERS = {
  assortment_page: 1,
  assortment_page_size: 10,
  branches_page: 1,
  branches_page_size: 10,
  price_list_page: 1,
  price_list_page_size: 10,
  ...Object.fromEntries(COLUMNS_ALL.map(({ key }) => [`all_${key}`, []])),
  ...Object.fromEntries(COLUMNS_BRANCHES.map(({ key }) => [`branches_${key}`, []])),
  ...Object.fromEntries(COLUMNS_PRICE.map(({ key }) => [`price_${key}`, []])),
};

export default function AssortmentPage() {
  const { fetchUserInfo } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploadedSuccessfully, setIsUploadedSuccessfully] = useState(false);
  const [isAssortmentUploaded, setIsAssortmentUploaded] = useState(false);
  const [isBranchesUploaded, setIsBranchesUploaded] = useState(false);
  const [isSalesUploaded, setIsSalesUploaded] = useState(false);
  const [isPriceListUploaded, setIsPriceListUploaded] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [assortmentTotalItems, setAssortmentTotalItems] = useState(0);
  const [pricesTotalItems, setPricesTotalItems] = useState(0);
  const [branchesTotalItems, setBranchesTotalItems] = useState(0);
  const [assortmentData, setAssortmentData] = useState([]);
  const [pricesData, setPricesData] = useState([]);
  const [originalPricesData, setOriginalPricesData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [branchNames, setBranchNames] = useState([]);
  const [editingBranchCells, setEditingBranchCells] = useState({});
  const [editingPriceListRows, setEditingPriceListRows] = useState({});
  const [editingAssortmentListRows, setEditingAssortmentListRows] = useState({});

  const assortmentKey = (row, index) => `${row.sku_code}_${index}`;
  const branchCellKey = (row, branchName) => `${row.sku_code}_${branchName}`; 
  const priceListRowKey = (row, index) => `priceList_${index}`;

  const [statusOptions, setStatusOptions] = useState([]);
  const [assortmentError, setAssortmentError] = useState("")
  const [assortmentFilterOptions, setAssortmentFilterOptions] = useState({});
  const [branchesFilterOptions, setBranchesFilterOptions] = useState({});
  const [priceListFilterOptions, setPriceListFilterOptions] = useState({});

  // --- Filters State and Logic ---
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

  const fetchAssortmentData = useCallback(async () => {
    setIsLoadingData(true);
    const columnFilters = {};
    COLUMNS_ALL.forEach(({ key }) => {
      const val = filters[`all_${key}`];
      if (val?.length) columnFilters[key] = val;
    });

    assortmentService.getAssortmentData(filters.assortment_page, filters.assortment_page_size, undefined, columnFilters)
      .then(response => {
        if(response.items){
          setAssortmentData(response.items);
          setAssortmentTotalItems(response.total_items)
        }
        if(response.filter_options){
          setAssortmentFilterOptions(response.filter_options);
        }
      })
      .catch(error => {
        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => {setIsLoadingData(false)});

    assortmentService.getStatusOptions()
      .then(response => { if(response) setStatusOptions(response); })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.assortment_page, filters.assortment_page_size,
    filters.all_sku_code, filters.all_sku_name, filters.all_mother_sku,
    filters.all_pieces_in_master_carton, filters.all_master_carton_volume_cbm,
    filters.all_master_carton_gross_weight_kg, filters.all_lead_time,
    filters.all_source, filters.all_general_stock_norm_days, filters.all_status,
  ]);

  useEffect(() => {
    fetchAssortmentData();
  }, [fetchAssortmentData]);

  const fetchBranchestData = useCallback(async () => {
    setIsLoadingData(true);
    const columnFilters = {};
    COLUMNS_BRANCHES.forEach(({ key }) => {
      const val = filters[`branches_${key}`];
      if (val?.length) columnFilters[key] = val;
    });

    assortmentService.getBranchesData(filters.branches_page, filters.branches_page_size, columnFilters)
      .then(response => {
        if(response.items){
          const grouped = assortmentService.groupBySkuForBranches(response.items)
          setBranchesData(grouped)
          setBranchNames([
            ...new Set(response.items.map(item => item.branch_name))
          ]);
          setBranchesTotalItems(response.total_items)
        }
        if(response.filter_options){
          setBranchesFilterOptions(response.filter_options);
        }
      })
      .catch(error => {
        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => {setIsLoadingData(false)});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.branches_page, filters.branches_page_size,
    filters.branches_sku_code, filters.branches_sku_name, filters.branches_mother_sku,
  ]);

  useEffect(() => {
    fetchBranchestData();
  }, [fetchBranchestData]);


  const fetchPriceListData = useCallback(async () => {
    setIsLoadingData(true);
    const columnFilters = {};
    COLUMNS_PRICE.forEach(({ key }) => {
      const val = filters[`price_${key}`];
      if (val?.length) columnFilters[key] = val;
    });

    assortmentService.getPriceListData(filters.price_list_page, filters.price_list_page_size, columnFilters)
      .then(response => {
        if(response.items){
          setPricesData(response.items);
          setOriginalPricesData(response.items)
          setPricesTotalItems(response.total_items)
        }
        if(response.filter_options){
          setPriceListFilterOptions(response.filter_options);
        }
      })
      .catch(error => {
        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => {setIsLoadingData(false)});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.price_list_page, filters.price_list_page_size,
    filters.price_sku_code, filters.price_sku_name, filters.price_date,
    filters.price_invoice_price, filters.price_dsp,
  ]);

  useEffect(() => {
    console.log("useEffect fetchPriceListData ")
    fetchPriceListData();
  }, [fetchPriceListData]);

  const handleUpdateBranchStock = (branchName, sku_code, value) => {
    assortmentService.updateBranchStock(sku_code, branchName, value)
      .then(response => {
        console.log("updateBranchStock ", response);
        if(response.rows_updated){
          setIsDataUpdated(true);
        }
      })
      .catch(error => {
        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => fetchBranchestData())
  }
  
  const handleStockNormDaysChange = (rowIndex, newStockNormDays) => {
    assortmentService.updateAssortmentNormDays(assortmentData[rowIndex].sku_code, newStockNormDays)
      .then(response => {
        console.log("response ", response)
        if(response.rows_updated){
          setIsDataUpdated(true);
        }
      })
      .catch(error => {
        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => fetchAssortmentData())
  };

  const handleUpdatePriceList = (sku_code, original_date, date, invoice_price, dsp) => {
    assortmentService.updatePriceList(sku_code, original_date, date, invoice_price, dsp)
      .then(response => {
        console.log("updatePriceList ", response);
        if(response.rows_updated && response.rows_updated > 0){
          setIsDataUpdated(true);
        }else{
          setAssortmentError("Не удалось редактировать");
        }
      })
      .catch(error => {
        console.log("error ", error)

        const errorMessage = parseApiError(error)
        setAssortmentError(errorMessage ? errorMessage : "");
      })
      .finally(() => {
        fetchPriceListData();
      })
  }

  const handleAssortmentUploaded = (success) => {
    console.log("assortment uploaded ", success);
    setIsAssortmentUploaded(success);
    if(success && isBranchesUploaded && isPriceListUploaded && isSalesUploaded){
      handleExcelUplaodedSuccessfully();
    }else{
      setIsUploadedSuccessfully(false);
    }
  }
  const handleBranchUploaded = (success) => {
    console.log("branch uploaded");
    setIsBranchesUploaded(success);
    if(success && isAssortmentUploaded && isPriceListUploaded && isSalesUploaded){
      handleExcelUplaodedSuccessfully();
    }else{
      setIsUploadedSuccessfully(false);
    }
  }
  const handleSalesUploaded = (success) => {
    console.log("sales uploaded");
    setIsSalesUploaded(success);
    if(success && isBranchesUploaded && isPriceListUploaded && isAssortmentUploaded){
      handleExcelUplaodedSuccessfully();
    }else{
      setIsUploadedSuccessfully(false);
    }
  }
  const handlePriceUploaded = (success) => {
    console.log("price uploaded");
    setIsPriceListUploaded(success);
    if(success && isBranchesUploaded && isAssortmentUploaded && isSalesUploaded){
      handleExcelUplaodedSuccessfully();
    }else{
      setIsUploadedSuccessfully(false);
    }
  }

  const handleExcelUplaodedSuccessfully = async () => {
    setIsLoadingData(true);
    await fetchAssortmentData();
    await fetchBranchestData();
    await fetchPriceListData();

    setIsLoadingData(true);
    setIsUploadOpen(false);
    setTimeout(async () => {
      await fetchUserInfo();
      setIsUploadedSuccessfully(true);
      setIsLoadingData(false);
    }, 2000);
  }
  
  const handleStatusChange = (rowIndex, newStatus) => {
    if(assortmentData[rowIndex].status !== newStatus){
      assortmentData[rowIndex].status = newStatus;
    
      assortmentService.updateAssortmentStatus(assortmentData[rowIndex].sku_code, newStatus)
        .then(response => {
          if(response.rows_updated){
            setIsDataUpdated(true);
          }
        })
        .catch(error => {
          const errorMessage = parseApiError(error)
          setAssortmentError(errorMessage ? errorMessage : "");
        })
        .finally(() => fetchAssortmentData())
    }
    setOpenStatusDropdown(null);
  };

  const handleDropdownToggle = (tableType, rowIndex, event) => {
    event.stopPropagation();
    const key = `${tableType}-${rowIndex}`;
    console.log("handleDropdownToggle key ", key)
    if (openStatusDropdown === key) {
      setOpenStatusDropdown(null);
    } else {
      setOpenStatusDropdown(key)
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenStatusDropdown(null);
    };
    if (openStatusDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openStatusDropdown]);
  
  const cellRefs = React.useRef({});

  // ── Column filter helpers ─────────────────────────────────────────────────────
  const allColOptions = useMemo(() => {
    const result = {};
    Object.entries(assortmentFilterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [assortmentFilterOptions]);

  const branchesColOptions = useMemo(() => {
    const result = {};
    Object.entries(branchesFilterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [branchesFilterOptions]);

  const priceColOptions = useMemo(() => {
    const result = {};
    Object.entries(priceListFilterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [priceListFilterOptions]);

  const handleAllColFilterApply = (key, vals) => updateFilters({ [`all_${key}`]: vals, assortment_page: 1 }, false);
  const getAllColSelected = (key) => filters[`all_${key}`] || [];

  const handleBranchesColFilterApply = (key, vals) => updateFilters({ [`branches_${key}`]: vals, branches_page: 1 }, false);
  const getBranchesColSelected = (key) => filters[`branches_${key}`] || [];

  const handlePriceColFilterApply = (key, vals) => updateFilters({ [`price_${key}`]: vals, price_list_page: 1 }, false);
  const getPriceColSelected = (key) => filters[`price_${key}`] || [];

  const renderStatusCell = (row, index, tableType) => {
    const dropdownKey = `${tableType}-${index}`;
    const isOpen = openStatusDropdown === dropdownKey;

    const cellAnchorRef = (el) => { cellRefs.current[dropdownKey] = el; };
    
    return (
      <div 
        style={{
          position: 'relative',
          cursor: "pointer",
          borderBottomRightRadius: index === assortmentData.length - 1 ? "20px" : "0px"
        }}
        className={isOpen ? 'dropdown-container-parent' : ''}
        ref={cellAnchorRef} 
        onClick={(e) => handleDropdownToggle(tableType, index, e)}
      >
        <div className="data-table-column">
          {
            row.status?.toLowerCase() === 'активный' ? <ActiveIcon width={20}/> 
              : row.status?.toLowerCase() === 'новый' ? <NewIcon width={20}/> 
                : row.status?.toLowerCase() === 'неактивный' ? <DiscnIcon width={20}/> 
                  : row.status?.toLowerCase() === 'на вывод' ? <TbdICon width={20}/> 
                    : <span style={{width: "20px"}}></span>
          }
          <span style={{
            color: row.status?.toLowerCase() !== 'активный' && row.status?.toLowerCase() !== 'новый' && row.status?.toLowerCase() !== 'на вывод' && row.status?.toLowerCase() !== 'неактивный' ? "#DC1A00" : "#404040"
          }}>
            {row.status}
          </span>
          <div 
            style={{ display: 'flex', alignItems: 'center' }}
            className='drop-down'
          >
            <DropdownIcon color='#010101' className='drop-down'/>
          </div>
        </div>
        {isOpen && 
          <StatusDropdownPortal
            anchorEl={cellRefs.current[dropdownKey]}
            options={statusOptions}
            onSelect={(option) => handleStatusChange(index, option)}
            onClose={() => setOpenStatusDropdown(null)}
          />
        }
      </div>
    );
  };

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content assortment-page">
        <NotificationContainer>{
            isDataUpdated && 
              <NotificationModal 
                closeModal={() => setIsDataUpdated(false)} 
                type="success" 
                title="Успешно обновлен" 
                duration={2000} 
              />
          }
          {
            assortmentError && <NotificationModal closeModal={() => setAssortmentError(null)} type="error" subTitle={assortmentError} title="Ошибка" duration={3000}/>
          }
          {
            isUploadedSuccessfully && 
              <NotificationModal 
                closeModal={() => setIsUploadedSuccessfully(false)} 
                type="success" 
                title="Файлы Excel успешно загружены" 
                duration={2500}
              />
          }
        </NotificationContainer>

        <div className="page-header-container">
            <h1 className="assortment-title">Вводные</h1>
            <div className='assortment-header-buttons'>
              <button type="button" className="secondary-button" 
                onClick={() => {
                  setIsUploadOpen(true);
                  setIsAssortmentUploaded(false);
                  setIsBranchesUploaded(false);
                  setIsSalesUploaded(false);
                  setIsPriceListUploaded(false);
                }}
              >
                <UploadIcon size={24} />
                Загрузить Excel
              </button>
              {
                isUploadOpen && 
                  <UploadExcelModal 
                    onAssortmentUploaded={handleAssortmentUploaded} 
                    onBranchUploaded={handleBranchUploaded} 
                    onSalesUploaded={handleSalesUploaded} 
                    onPriceUploaded={handlePriceUploaded} 
                    closeModal={() => {
                      setIsUploadOpen(false); 
                      fetchAssortmentData(); 
                      fetchBranchestData(); 
                      fetchPriceListData()
                    }}
                  />
              }
            </div>
        </div>

        <div className='page-subheading-row'>
          <div className='assortment-header-progress-text'>
            Чтобы начать, заполните статусы по каждому товару и подразделению
          </div>
        </div>

        <div className='tabs-container'>
          <button className={`tabs-item-container ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab("all")} >
            <div className={`tabs-item ${activeTab === 'all' ? 'active' : ''}`}>
              Весь ассортимент
            </div>
          </button>
          
          <button className={`tabs-item-container ${activeTab === 'branches-matrix' ? "active" : ""}`} onClick={() => setActiveTab("branches-matrix")}>
            <div className={`tabs-item ${activeTab === 'branches-matrix' ? "active" : ""}`}>
              Ассортиментная матрица по филиалам
            </div>
          </button>

          <button className={`tabs-item-container ${activeTab === 'price-list' ? "active" : ""}`} onClick={() => setActiveTab("price-list")}>
            <div className={`tabs-item ${activeTab === 'price-list' ? "active" : ""}`}>
              Прайс-лист
            </div>
          </button>
        </div>

        {
          activeTab === "all" && 
          <>
            <div className="table-outer-wrapper outer-scrollbar">
              <div className="sticky-table-wrapper custom-scrollbar">
                <table className="sticky-table all-table">
                  <thead>
                    <tr>
                      {COLUMNS_ALL.map(({ key, label }) => {
                        const hasFilter = key in assortmentFilterOptions;
                        const filterBtn = hasFilter && <ColumnFilterDropdown label={label} options={allColOptions[key] || []} selected={getAllColSelected(key)} onApply={(vals) => handleAllColFilterApply(key, vals)} />;
                        if (key === 'sku_code') return (
                          <th key={key} className="sticky left l1" style={{borderTopLeftRadius: "20px"}}>
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}>
                              <span>{label}</span>{filterBtn}
                            </div>
                          </th>
                        );
                        if (key === 'sku_name') return (
                          <th key={key} className="sticky left l2">
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}><span>{label}</span>{filterBtn}</div>
                          </th>
                        );
                        if (key === 'mother_sku') return (
                          <th key={key} className="sticky left l3">
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}><span>{label}</span>{filterBtn}</div>
                          </th>
                        );
                        if (key === 'status') return (
                          <th key={key} className="sticky right">
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, width: '100%', }}><span>{label}</span>{filterBtn}</div>
                          </th>
                        );
                        return (
                          <th key={key} style={key === 'general_stock_norm_days' ? {minWidth: "150px"} : {}}>
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}><span>{label}</span>{filterBtn}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
        
                  <tbody>
                      { 
                        (assortmentData && assortmentData.length > 0) ? assortmentData.map((row, mainIndex) => {
                          const key = assortmentKey(row, mainIndex);
                          const isAssortmentEdit = editingAssortmentListRows[key] !== undefined && editingAssortmentListRows[key] !== false;
                          return (
                            <tr key={row.sku_code + mainIndex}>
                              <td className="sticky left l1">{row.sku_code}</td>
                              <td className="sticky left l2">{row.sku_name}</td>
                              <td className="sticky left l3">{row.mother_sku}</td>
                              <td>{row.pieces_in_master_carton?.toLocaleString("ru-RU")}</td>
                              <td>{row.master_carton_volume_cbm}</td>
                              <td>{row.master_carton_gross_weight_kg}</td>
                              <td>{row.lead_time}</td>
                              <td>{row.source}</td>
                              <td style={{width: "154px"}}>
                                <EditableCell
                                  value={row.general_stock_norm_days}
                                  isEditing={isAssortmentEdit}
                                  onChange={(e) => setAssortmentData(prev =>
                                    prev.map((item, index) =>
                                      index === mainIndex ? { ...item, general_stock_norm_days: e.target.value } : item
                                    )
                                  )}
                                  onEdit={() => setEditingAssortmentListRows(prev => ({ ...prev, [key]: row.general_stock_norm_days}))}
                                  onConfirm={() => {
                                    handleStockNormDaysChange(mainIndex, row.general_stock_norm_days);
                                    setEditingAssortmentListRows(prev => ({ ...prev, [key]: false }));
                                  }}
                                  onCancel={() => {
                                    setEditingAssortmentListRows(prev => ({ ...prev, [key]: false }));
                                    fetchBranchestData();
                                  }}
                                  editingContainerStyle={{maxWidth: "120px"}}
                                  displayContainerStyle={{maxWidth: "70px"}}
                                />
                              </td>
                              <td className="sticky right" style={{zIndex: openStatusDropdown === ("all-"+mainIndex) ? 10001 : 'inherit'}}>
                                  {renderStatusCell(row, mainIndex, 'all')}
                              </td>
                            </tr>
                          )}) : <tr key={"assortmentData-1"} >
                                  <td style={{margin: "auto", textAlign: 'center'}} colSpan="10">Нет данных</td>
                              </tr>
                      }
                  </tbody>
                </table>
              </div>
            </div>
            
            <Pagination
              wrapperStyle={{minWidth: 0}}
              currentPage={filters.assortment_page}
              totalItems={assortmentTotalItems}
              onPageChange={(p) => updateFilters({assortment_page: p})}
              rowsPerPage={filters.assortment_page_size}
              onRowsPerPageChange={(r) => { updateFilters({assortment_page_size: r}) }}
              rowsPerPageOptions={[5, 10, 20, 50]}
            />
          </>
        }

        {
          activeTab === "branches-matrix" && 
            <>
              <div className="table-outer-wrapper outer-scrollbar">
                <div className="sticky-table-wrapper custom-scrollbar">
                  <table className="sticky-table branch-matrix-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} className="sticky left l1" style={{borderTopLeftRadius: "20px", boxShadow: "-12px -20px 0px 0px #f8f9fa", borderLeft: "1px solid #DEDEDE;"}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                            <span>Код СКЮ</span>
                            {'sku_code' in branchesFilterOptions && <ColumnFilterDropdown label="Код СКЮ" options={branchesColOptions['sku_code'] || []} selected={getBranchesColSelected('sku_code')} onApply={(vals) => handleBranchesColFilterApply('sku_code', vals)} />}
                          </div>
                        </th>
                        <th rowSpan={2} className="sticky left l2">
                          <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                            <span>Наименование</span>
                            {'sku_name' in branchesFilterOptions && <ColumnFilterDropdown label="Наименование" options={branchesColOptions['sku_name'] || []} selected={getBranchesColSelected('sku_name')} onApply={(vals) => handleBranchesColFilterApply('sku_name', vals)} />}
                          </div>
                        </th>
                        <th rowSpan={2} className="sticky left l3">
                          <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                            <span>Базовый СКЮ</span>
                            {'mother_sku' in branchesFilterOptions && <ColumnFilterDropdown label="Базовый СКЮ" options={branchesColOptions['mother_sku'] || []} selected={getBranchesColSelected('mother_sku')} onApply={(vals) => handleBranchesColFilterApply('mother_sku', vals)} />}
                          </div>
                        </th>
                        <th
                          colSpan={branchNames.length}
                          className='branches-title-container'
                        >
                          <div className="branches-title">
                            Филиалы (Общая норма стока - дни)
                          </div>
                        </th>
                      </tr>
          
                      <tr>
                        {
                          branchNames.map((branchName, index) => (
                            <th className="branch-cell" style={{
                              top: "45px", 
                              borderRadius: "0px",
                              borderLeft: "0px",
                            }} key={index}>
                              {branchName}
                            </th>
                          ))
                        }
                      </tr>
                    </thead>
          
                    <tbody>
                        { 
                          (branchesData && branchesData.length > 0) ? branchesData.map((row, mainIndex) => (
                            <tr key={row.sku_code + mainIndex}>
                              <td className="sticky left l1">{row.sku_code}</td>
                              <td className="sticky left l2">{row.sku_name}</td>
                              <td className="sticky left l3">{row.mother_sku}</td>
                              {
                                branchNames.map((branchName, index) => {
                                  const key = branchCellKey(row, branchName);
                                  const isEdit = editingBranchCells[key] !== undefined && editingBranchCells[key] !== false;
                                  return (
                                    <td key={index} className="branch-cell">
                                      <EditableCell
                                        value={row[branchName]}
                                        isEditing={isEdit}
                                        onChange={(e) => {
                                          const updated = [...branchesData];
                                          updated[mainIndex][branchName] = e.target.value;
                                          setBranchesData(updated);
                                        }}
                                        onEdit={() => setEditingBranchCells(prev => ({ ...prev, [key]: row[branchName] }))}
                                        onConfirm={() => {
                                          handleUpdateBranchStock(branchName, row.sku_code, row[branchName]);
                                          setEditingBranchCells(prev => ({ ...prev, [key]: false }));
                                        }}
                                        onCancel={() => {
                                          setEditingBranchCells(prev => ({ ...prev, [key]: false }));
                                          fetchBranchestData();
                                        }}
                                        renderValue={(v) => v != null
                                          ? v
                                          : <span style={{ color: "#DC1A00" }}>Не заполнено</span>
                                        }
                                        editingContainerStyle={{maxWidth: "140px"}}
                                        displayContainerStyle={{width: "70px"}}
                                      />
                                    </td>
                                  )})
                              }
                            </tr>
                          )) : <tr key={"branchesData-1"} >
                                  <td style={{margin: "auto", textAlign: 'center'}} colSpan="9">Нет данных</td>
                              </tr>
                        }
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                wrapperStyle={{minWidth: 0}}
                currentPage={filters.branches_page}
                totalItems={branchesTotalItems}
                onPageChange={(p) => updateFilters({branches_page: p})}
                rowsPerPage={filters.branches_page_size}
                onRowsPerPageChange={(r) => { updateFilters({branches_page_size: r}) }}
                rowsPerPageOptions={[5, 10, 20, 50]}
              />
            </>
        }

        {
          activeTab === "price-list" && 
          <>
              <div className="data-table-container price-list-table">
                <table className="data-table">
                  <thead>
                    <tr>
                      {COLUMNS_PRICE.map(({ key, label }) => {
                        const hasFilter = key in priceListFilterOptions;
                        return (
                          <th key={key}>
                            <div style={{display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}>
                              <span>{label}</span>
                              {hasFilter && <ColumnFilterDropdown label={label} options={priceColOptions[key] || []} selected={getPriceColSelected(key)} onApply={(vals) => handlePriceColFilterApply(key, vals)} />}
                            </div>
                          </th>
                        );
                      })}
                      <th key="actions" style={{ width: 1 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    { 
                      (pricesData && pricesData.length > 0) ? pricesData.map((row, mainIndex) => {
                        const key = priceListRowKey(row, mainIndex);
                        const isPriceEdit = !!editingPriceListRows[key];

                        return (
                          <tr key={"price-list-" + mainIndex}>
                            <td style={{}}>
                              {row.sku_code}
                            </td>
                            <td style={{}}>{row.sku_name}</td>
                            <td style={{width: "180px"}}>
                              <EditableCell
                                type="date"
                                value={row.date}
                                isEditing={isPriceEdit}
                                showControls={false}
                                onChange={(e) => setPricesData(prev =>
                                  prev.map((item, index) =>
                                    index === mainIndex ? { ...item, date: e.target.value } : item
                                  )
                                )}
                                renderValue={(v) => formatDate(v)}
                              />
                            </td>
                            <td style={{width: "200px"}}>
                              <EditableCell
                                value={row.invoice_price}
                                isEditing={isPriceEdit}
                                showControls={false}
                                onChange={(e) => setPricesData(prev =>
                                  prev.map((item, index) =>
                                    index === mainIndex ? { ...item, invoice_price: e.target.value } : item
                                  )
                                )}
                                renderValue={(v) => <>{v?.toLocaleString("ru-RU")} &#8376;</>}
                              />
                            </td>
                            <td style={{width: "200px"}}>
                              <EditableCell
                                value={row.dsp}
                                isEditing={isPriceEdit}
                                showControls={false}
                                onChange={(e) => setPricesData(prev =>
                                  prev.map((item, index) =>
                                    index === mainIndex ? { ...item, dsp: e.target.value } : item
                                  )
                                )}
                                renderValue={(v) => v?.toLocaleString("ru-RU")}
                              />
                            </td>
                            <td style={{ width: 80, whiteSpace: 'nowrap' }}>
                              <EditableCellActions
                                isEditing={isPriceEdit}
                                onEdit={() => setEditingPriceListRows(prev => ({ ...prev, [key]: row ?? "" }))}
                                onConfirm={() => {
                                  setEditingPriceListRows(prev => ({ ...prev, [key]: false }));
                                  handleUpdatePriceList(row.sku_code, originalPricesData[mainIndex].date, row.date, row.invoice_price, row.dsp);
                                }}
                                onCancel={() => {
                                  setEditingPriceListRows(prev => ({ ...prev, [key]: false }));
                                  fetchPriceListData();
                                }}
                              />
                            </td>
                          </tr>
                        )
                    }) : <tr key={"price-list-1"} >
                              <td style={{margin: "auto", textAlign: 'center'}} colSpan="6">Нет данных</td>
                          </tr>
                    }
                  </tbody>
                </table>
                <Pagination
                  currentPage={filters.price_list_page}
                  totalItems={pricesTotalItems}
                  onPageChange={(p) => updateFilters({price_list_page: p})}
                  rowsPerPage={filters.price_list_page_size}
                  onRowsPerPageChange={(r) => { updateFilters({price_list_page_size: r}) }}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                />
              </div>
                
          </>
        }

      </div>
    </>
  );
}
